import {
  EMdfOutcomeValueType,
  IMdfOutcomeItem,
  TMdfOutcomeItemValue,
} from '../model/mdfOutcomeItem'

type TJsonValue = string | number | boolean | null | TJsonValue[] | TJsonObject

interface TJsonObject {
  [key: string]: TJsonValue
}

const isJsonObject = (value: TJsonValue): value is TJsonObject =>
  typeof value === 'object' && value !== null && !Array.isArray(value)

export const isMdfOutcomeValueType = (value: string): value is EMdfOutcomeValueType => {
  switch (value) {
    case EMdfOutcomeValueType.Financial:
    case EMdfOutcomeValueType.Number:
    case EMdfOutcomeValueType.Percentage:
    case EMdfOutcomeValueType.FreeText:
      return true
    default:
      return false
  }
}

export const normalizeMdfOutcomeItemValue = (
  value: TMdfOutcomeItemValue,
  valueType: EMdfOutcomeValueType
): TMdfOutcomeItemValue => {
  if (valueType === EMdfOutcomeValueType.FreeText) {
    return value === null ? '' : String(value)
  }

  if (value === null || (typeof value === 'string' && value.trim() === '')) {
    return null
  }

  const numericValue = typeof value === 'number' ? value : Number(value.trim())
  if (!Number.isFinite(numericValue) || numericValue < 0) {
    return null
  }

  if (valueType === EMdfOutcomeValueType.Percentage && numericValue > 100) {
    return null
  }

  return numericValue
}

export const createMdfOutcomeItem = (): IMdfOutcomeItem => ({
  name: '',
  valueType: EMdfOutcomeValueType.FreeText,
  value: '',
})

const createLegacyTextOutcomeItem = (value: string): IMdfOutcomeItem => ({
  ...createMdfOutcomeItem(),
  value,
})

const mapJsonObjectToOutcomeItem = (item: TJsonObject): IMdfOutcomeItem => {
  const valueType =
    typeof item.valueType === 'string' && isMdfOutcomeValueType(item.valueType)
      ? item.valueType
      : EMdfOutcomeValueType.FreeText
  const rawValue =
    typeof item.value === 'string' || typeof item.value === 'number' ? item.value : null

  return {
    name: typeof item.name === 'string' ? item.name : '',
    valueType,
    value: normalizeMdfOutcomeItemValue(rawValue, valueType),
  }
}

export const parseMdfOutcome = (value?: string | null): IMdfOutcomeItem[] => {
  const rawValue = value?.trim() ?? ''
  if (!rawValue) {
    return []
  }

  let parsedValue: TJsonValue
  try {
    parsedValue = JSON.parse(rawValue) as TJsonValue
  } catch {
    // Existing MDF text is retained as a Free text row, not discarded.
    return [createLegacyTextOutcomeItem(value ?? '')]
  }

  if (typeof parsedValue === 'string') {
    return [createLegacyTextOutcomeItem(parsedValue)]
  }

  if (!Array.isArray(parsedValue)) {
    return [createLegacyTextOutcomeItem(value ?? '')]
  }

  return parsedValue.filter(isJsonObject).map(mapJsonObjectToOutcomeItem)
}

export const serializeMdfOutcome = (items: IMdfOutcomeItem[]): string => JSON.stringify(items)

export const normalizeMdfOutcome = (value?: string | null): string =>
  serializeMdfOutcome(parseMdfOutcome(value))
